import React, { useEffect, useMemo, useState } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { createCommand, COMMAND_PRIORITY_EDITOR, $getNodeByKey } from "lexical";
import {
  colors2,
  icons,
  SkeletonLoader,
  SwitchSingle,
  UI_BODY_SEMIBOLD,
  UI_CAPTION_SEMIBOLD_DF,
  useBannerContext,
} from "oolib";

import { findRange, getAllText, getSelectionRange } from "./utils";
import {
  $wrapSelectionInMarkNode,
  $unwrapMarkNode,
  $getMarkIDs,
} from "@lexical/mark";
import { useAnnoContextLex } from "../AnnoPlugin/context";
import { useGetAIAnnoSuggestions } from "./hooks";
import {
  CardWrapperStyled,
  SidebarContainerStyled,
  SidebarWrapperStyled,
} from "./styled";

// import data from "./data.json";
import { Cards } from "./components/card";

import { _Locale } from "@/components/locale/Locale";
import { useAppSettingsContext } from "@/contexts/appSettingsContext";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";
import { getMarkNodeMap } from "../AnnoPlugin";
import { buildHierarchicalTagTypesConfig } from "../AnnoPlugin/AnnoLightbox/utils/buildHierarchicalTagTypesConfig";

const { Stars, Info } = icons;

export const SHOW_ANNO_SUGGESTIONS = createCommand("SHOW_ANNO_SUGGESTIONS");

export const AnnoSuggestionsPlugin = ({
  annotation,
  fieldValuePath,
  _id,
  contentType,
  target,
}) => {
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLangDisplay = APP_SETTINGS.lang?.display;

  const [showSidebar, setShowSidebar] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [showTranslatedSuggestions, setShowTranslatedSuggestions] =
    useState(false);
  const [editor] = useLexicalComposerContext();
  const { handleSetAnnoData } = useAnnoContextLex();

  const markNodeMap = getMarkNodeMap(editor);

  const { SET_ALERT_BANNER } = useBannerContext();

  const tagCategory = annotation.tagTypesConfig.map((d) => d.tagType);

  const platformConfigs = useGetQueryData('platformConfigs');
  const shouldUseSMLightbox = (
    platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.enable === true ||
    platformConfigs?.deployment?._AnnotationFramework?.framework === 'tags_subThemes_themes'
  );
  const hierarchicalTagTypesConfig = useMemo(() => {
    return buildHierarchicalTagTypesConfig({
      shouldUseSMLightbox,
      annotationTagsCount: platformConfigs?.annotationTagsCount,
    });
  }, [shouldUseSMLightbox, platformConfigs?.annotationTagsCount]);

  const { data: suggestionsData, status } = useGetAIAnnoSuggestions({
    text: getAllText(editor),
    tagCategory,
    fieldValuePath,
    _id,
    contentType,
    fetchTrigger,
    showLogs: process.env.NODE_ENV !== "production",
  });

  useEffect(() => {
    editor.registerCommand(
      SHOW_ANNO_SUGGESTIONS,
      () => {
        setShowSidebar(true);
        setFetchTrigger((prev) => prev + 1);
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, []);

  useEffect(() => {
    if (status === "success" && suggestionsData) {
      // Legacy mode returns the suggestions as a flat array; dual-agent mode
      // wraps them: { annotations: [...], _isDualAgentResult: true, ... }.
      // Both shapes carry the same per-suggestion fields the Cards need
      // (_id, text, tags, justification).
      setSuggestions(
        Array.isArray(suggestionsData)
          ? suggestionsData
          : suggestionsData.annotations || []
      );
    }
  }, [status]);

  //TODO: optimization
  const handleApply = ({ text, tagsData, _id }) => {
    try {
      const { startNodeKey, endNodeKey, startOffset, endOffset } = findRange(
        editor,
        text
      );

      editor.update(() => {
        const selection = getSelectionRange({
          startNodeKey,
          endNodeKey,
          startOffset,
          endOffset,
        });

        const data = {};

        tagsData.forEach(({ tagCategory, tags }) => {
          data[tagCategory] = { collectionId: tagCategory, data: tags };
        });

        $wrapSelectionInMarkNode(selection, selection.isBackward, _id);
        handleSetAnnoData({
          type: "tags",
          data,
          dataId: _id,
          isAnnoMutation: true,
          createdVia: "humanInTheLoop",
        });
      });
    } catch (error) {
      SET_ALERT_BANNER(error.message, "red", 3000);
    }
  };

  const handleEditTags = (suggestionId, newTags) => {
    setSuggestions(prev =>
      prev.map(s => s._id === suggestionId ? { ...s, tags: newTags } : s)
    );
  };

  const handleRemove = ({ _id }) => {
    try {
      editor.update(() => {
        const nodeKeys = markNodeMap.get(_id);

        if (!nodeKeys) throw new Error("node found in the mark node map");

        const markNodesToChange = [];

        nodeKeys.forEach((key) => {
          const node = $getNodeByKey(key);
          markNodesToChange.push({
            nodeKey: key,
            node,
            dataIds: $getMarkIDs(node),
          });
        });

        const markNodesToUnwrap = markNodesToChange.filter(
          (d) => d.dataIds.length === 1 && d.dataIds[0] === _id
        );
        const markNodesToEdit = markNodesToChange.filter(
          (d) => d.dataIds.length > 1 && d.dataIds.includes(_id)
        );

        markNodesToUnwrap.forEach(({ node }) => {
          $unwrapMarkNode(node);
        });
        markNodesToEdit.forEach(({ node, nodeKey }) => {
          node.deleteID(_id);

          markNodeMap.delete(_id);
        });
      });
    } catch (error) {
      SET_ALERT_BANNER("failed to remove!", "red", 3000);
    }
  };

  return showSidebar ? (
    <SidebarContainerStyled
      initial={{ width: 0, opacity: 1, height: "100%" }}
      animate={{ width: 400 }}
      transition={{ type: "tween" }}
      exit={{ width: 0, opacity: 0 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBlock: "1.4rem",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.4rem",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.6rem",
              alignItems: "center",
              padding: "0.2rem 0rem",
            }}
          >
            <Stars size="16" />

            <UI_BODY_SEMIBOLD>
              {" "}
              Annotation Suggestions{" "}
              {`${suggestions.length ? `(${suggestions.length})` : ""}`}
            </UI_BODY_SEMIBOLD>
          </div>
          {target && (
            <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey60 }}>
              {_Locale(`For: <word1 : ${target}>`)}
            </UI_CAPTION_SEMIBOLD_DF>
          )}
        </div>

        <ButtonGhost
          S
          iconSize={"S"}
          icon="X"
          onClick={() => setShowSidebar(false)}
        />
      </div>

      <SidebarWrapperStyled>
        {status === "loading" ? (
          <>
            <SkeletonLoader
              style={{ width: "100%", height: "25rem", borderRadius: "0.8rem" }}
            />
            <SkeletonLoader
              style={{ width: "100%", height: "25rem", borderRadius: "0.8rem" }}
            />
            <SkeletonLoader
              style={{ width: "100%", height: "25rem", borderRadius: "0.8rem" }}
            />
            <SkeletonLoader
              style={{ width: "100%", height: "25rem", borderRadius: "0.8rem" }}
            />
          </>
        ) : (
          <div
            style={{
              marginBottom: "5rem",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              width: "100%",
            }}
          >
            {suggestions.some((data) => data.translations) && (
              <CardWrapperStyled style={{ borderRadius: "0.6rem" }}>
                <SwitchSingle
                  option={{
                    value: true,
                    display: _Locale(
                      `Show annotation suggestions in <word1 : ${activeLangDisplay}>`
                    ),
                  }}
                  value={showTranslatedSuggestions}
                  onChange={(id, value) => setShowTranslatedSuggestions(value)}
                  saveValueAsString
                />
              </CardWrapperStyled>
            )}
            {suggestions.map((d) => (
              <Cards
                {...d}
                key={d._id}
                showTranslatedSuggestions={showTranslatedSuggestions}
                applied={markNodeMap.get(d._id)}
                handleApply={handleApply}
                handleRemove={handleRemove}
                handleEditTags={handleEditTags}
                tagTypesConfig={hierarchicalTagTypesConfig}
              />
            ))}
          </div>
        )}
      </SidebarWrapperStyled>
    </SidebarContainerStyled>
  ) : null;
};
